/**
 * 로그인 URL 규약 (docs/04-authentication.md §3.1.1).
 *
 * - `/login/{tenantCode}` — 해당 테넌트로 고정 로그인 (입력 필드 숨김, SaaS 기준).
 * - `/login/all` — 테넌트 코드를 직접 입력 (기존 SaaS 동작).
 * - `/login` — 배포 모드에 따라 `/login/all` 또는 `/login/{default}` 로 리다이렉트.
 */

export const LOGIN_TENANT_CODE_ALL = 'all' as const;

/** localStorage — 새 탭·재방문·로그아웃·세션 만료 후에도 마지막 테넌트 로그인 URL 유지 */
export const LAST_LOGIN_TENANT_STORAGE_KEY = 'ax:last-login-tenant-code' as const;

export type DeploymentMode = 'saas' | 'on-prem';

function trimOrEmpty(value: string | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 인증 후 app path 에 현재 테넌트 prefix 부여 (네비게이션 표준 진입점).
 *
 * - `tenantCode` 는 trim 후 `encodeURIComponent`.
 * - `appPath` 가 `/` 면 `/{seg}`, 아니면 `/{seg}{앞에 / 보장한 appPath}`.
 * - `tenantCode` 가 빈 문자열이면 prefix 없이 `appPath` 를 그대로 반환(가드 안전).
 */
export function tenantPath(appPath: string, tenantCode: string): string {
  const trimmed = tenantCode.trim();
  if (trimmed === '') {
    return appPath;
  }
  const seg = encodeURIComponent(trimmed);
  const p = appPath.startsWith('/') ? appPath : `/${appPath}`;
  return p === '/' ? `/${seg}` : `/${seg}${p}`;
}

/**
 * prefix 분리 — `/ax/erp/x` → `/erp/x`, `/ax` → `/`, `/` → `/`.
 *
 * 첫 세그먼트를 무조건 제거한다(가드에서 이미 prefix 로 간주되는 컨텍스트에서만 호출).
 * 쿼리스트링/해시는 보존한다.
 */
export function stripTenantPrefix(fullPath: string): string {
  const hashIndex = fullPath.indexOf('#');
  const hash = hashIndex >= 0 ? fullPath.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? fullPath.slice(0, hashIndex) : fullPath;
  const queryIndex = withoutHash.indexOf('?');
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';
  const pathOnly = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;

  const segments = pathOnly.split('/').filter((s) => s.length > 0);
  segments.shift();
  const appPath = segments.length > 0 ? `/${segments.join('/')}` : '/';
  return `${appPath}${query}${hash}`;
}

export function resolveDefaultLoginTenantCode(explicit?: string): string {
  return (
    trimOrEmpty(explicit) || trimOrEmpty(import.meta.env.VITE_AX_DEFAULT_TENANT_CODE) || 'platform'
  );
}

/** `/login` 만 열었을 때 이동할 path 세그먼트 (`all` 또는 on-prem 기본 코드). */
export function defaultLoginPathTenantCode(
  mode: DeploymentMode,
  defaultTenantCode?: string,
): string {
  if (mode === 'on-prem') {
    return resolveDefaultLoginTenantCode(defaultTenantCode);
  }
  return LOGIN_TENANT_CODE_ALL;
}

export function rememberLastLoginTenantCode(code: string | null | undefined): void {
  const trimmed = trimOrEmpty(code ?? '');
  if (!trimmed || trimmed === LOGIN_TENANT_CODE_ALL) return;
  try {
    localStorage.setItem(LAST_LOGIN_TENANT_STORAGE_KEY, trimmed);
  } catch {
    // storage unavailable (private mode 등)
  }
}

export function readLastLoginTenantCode(): string | null {
  try {
    const stored = localStorage.getItem(LAST_LOGIN_TENANT_STORAGE_KEY);
    const trimmed = trimOrEmpty(stored ?? '');
    if (!trimmed || trimmed === LOGIN_TENANT_CODE_ALL) return null;
    return trimmed;
  } catch {
    return null;
  }
}

/**
 * 로그아웃·401·미인증 리다이렉트 시 `/login/{code}` 세그먼트 결정.
 * 우선순위: 현재 user.tenantCode → URL(고정 코드) → sessionStorage → 모드 기본값.
 */
export function resolveLoginRedirectTenantCode(options: {
  mode: DeploymentMode;
  userTenantCode?: string | null;
  routeTenantCode?: string | null;
  defaultTenantCode?: string;
}): string {
  const fromUser = trimOrEmpty(options.userTenantCode ?? '');
  if (fromUser) return fromUser;

  const fromRoute = trimOrEmpty(options.routeTenantCode ?? '');
  if (fromRoute && fromRoute !== LOGIN_TENANT_CODE_ALL) return fromRoute;

  const fromStorage = readLastLoginTenantCode();
  if (fromStorage) return fromStorage;

  return defaultLoginPathTenantCode(options.mode, options.defaultTenantCode);
}

/** redirect 경로(`/{seg}/...`)의 첫 세그먼트(테넌트 코드 후보)를 디코드해 반환. */
export function redirectTenantSegment(fullPath: string): string {
  const pathOnly = fullPath.split(/[?#]/, 1)[0] ?? '';
  const first = pathOnly.split('/').find((s) => s.length > 0) ?? '';
  try {
    return decodeURIComponent(first);
  } catch {
    return first;
  }
}

export interface PostLoginRedirectProbe {
  /** vue-router resolve 결과가 catch-all(not-found)인지 */
  isNotFound: boolean;
  /** `/:tenantCode` 서브트리 매칭 여부 (`params.tenantCode` 정의됨) */
  isTenantSubtree: boolean;
}

/**
 * 로그인 성공 후 이동 경로 결정 (설계 tenant-url-prefix-persistence F-2/F-3/F-4).
 *
 * - redirect 첫 세그먼트가 로그인 테넌트와 일치(prefix 경로) → 원경로 복귀.
 * - prefix 없이 등록된 top-level 인증 라우트(예: MES `/pop/*`, 리포트 print)는
 *   실제 라우트로 매칭되고 `/:tenantCode` 서브트리가 아닐 때만 원경로 복귀 —
 *   가드 (3)의 top-level 통과 판정과 동일 기준. (미판정 시 무조건 대시보드로
 *   강등되어 `?redirect=/pop/...` 가 무시되던 버그의 수정)
 * - 그 외(타 테넌트 prefix·not-found·비경로 문자열)는 자기 대시보드로 강등(보안).
 */
export function resolvePostLoginRedirect(options: {
  redirectQuery: unknown;
  tenantCode: string;
  probe: (path: string) => PostLoginRedirectProbe;
}): string {
  const { redirectQuery, tenantCode, probe } = options;
  const fallback = tenantPath('/', tenantCode);
  if (typeof redirectQuery !== 'string' || !redirectQuery.startsWith('/')) return fallback;
  if (redirectTenantSegment(redirectQuery) === tenantCode) return redirectQuery;
  const probed = probe(redirectQuery);
  if (!probed.isNotFound && !probed.isTenantSubtree) return redirectQuery;
  return fallback;
}

export function buildLoginPath(
  tenantCode: string,
  query?: Record<string, string | undefined>,
): string {
  const segment = encodeURIComponent(tenantCode.trim());
  const base = `/login/${segment}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function isLoginRoutePath(path: string): boolean {
  return path === '/login' || /^\/login\/[^/]+$/.test(path);
}

export interface LoginRouteTenantState {
  /** URL path 세그먼트 (예: `platform`, `all`) */
  urlTenantCode: string;
  /** SaaS + `/login/all` 일 때만 true */
  showTenantCodeField: boolean;
}

/**
 * 라우트 `tenantCode` 파라미터 → 로그인 폼 표시/요청용 테넌트 코드 해석.
 */
export function resolveLoginRouteTenantState(
  tenantCodeParam: string | string[] | undefined,
  options: {
    mode: DeploymentMode;
    defaultTenantCode?: string;
    formTenantCode?: string;
  },
): LoginRouteTenantState {
  const raw = Array.isArray(tenantCodeParam) ? tenantCodeParam[0] : tenantCodeParam;
  const urlTenantCode = decodeURIComponent(trimOrEmpty(raw));

  if (options.mode === 'on-prem') {
    const fixed = resolveDefaultLoginTenantCode(options.defaultTenantCode);
    return { urlTenantCode: fixed, showTenantCodeField: false };
  }

  if (!urlTenantCode || urlTenantCode === LOGIN_TENANT_CODE_ALL) {
    return { urlTenantCode: LOGIN_TENANT_CODE_ALL, showTenantCodeField: true };
  }

  return { urlTenantCode, showTenantCodeField: false };
}

/** 로그인 API 요청에 실을 tenantCode (on-prem·고정 URL·all 입력 완료 후). */
export function tenantCodeForLoginRequest(
  state: LoginRouteTenantState,
  options: {
    mode: DeploymentMode;
    defaultTenantCode?: string;
    formTenantCode: string;
  },
): string {
  if (options.mode === 'on-prem') {
    return resolveDefaultLoginTenantCode(options.defaultTenantCode);
  }
  if (state.showTenantCodeField) {
    return trimOrEmpty(options.formTenantCode);
  }
  return state.urlTenantCode;
}
